/**
 * Data model for Markdown to HTML conversion feature
 * Feature: 001-markdown-html-converter
 */

// ============================================================================
// Core Entities
// ============================================================================

export interface ConversionResult {
  /** The converted HTML string (sanitized) */
  html: string;
  
  /** Original markdown input used for conversion */
  originalMarkdown: string;
  
  /** Timestamp when conversion was performed */
  convertedAt: Date;
  
  /** Conversion success status */
  success: boolean;
  
  /** Warnings encountered during conversion (non-fatal) */
  warnings: ConversionWarning[];
  
  /** Error information if conversion failed */
  error?: ConversionError;
  
  /** Metadata about the conversion process */
  metadata: ConversionMetadata;
}

export interface ConversionOptions {
  /** Enable GitHub Flavored Markdown extensions */
  enableGfm: boolean;
  
  /** Enable automatic XSS sanitization (always true for security) */
  sanitize: boolean;
  
  /** Allow raw HTML pass-through (dangerous, default: false) */
  allowRawHtml: boolean;
  
  /** Break on single line break (GFM style) */
  breaks: boolean;
  
  /** Enable syntax highlighting for code blocks */
  highlightCode: boolean;
  
  /** Custom CSS class prefix for generated HTML elements */
  cssClassPrefix?: string;
  
  /** Maximum input size in bytes (default: 1048576 = 1MB) */
  maxInputSize: number;
}

export interface ConversionWarning {
  /** Warning severity level */
  level: 'info' | 'warn';
  
  /** Human-readable warning message */
  message: string;
  
  /** Warning code for programmatic handling */
  code: ConversionWarningCode;
  
  /** Location in the markdown where warning occurred (if applicable) */
  location?: {
    line: number;
    column: number;
  };
}

export interface ConversionError {
  /** Error type */
  type: ConversionErrorType;
  
  /** Human-readable error message */
  message: string;
  
  /** Technical error details (for logging) */
  details?: string;
  
  /** Original error object if applicable */
  originalError?: Error;
}

export interface ConversionMetadata {
  /** Conversion processing time in milliseconds */
  processingTimeMs: number;
  
  /** Input size in bytes */
  inputSizeBytes: number;
  
  /** Output size in bytes */
  outputSizeBytes: number;
  
  /** Number of markdown elements parsed */
  elementCount: {
    headers: number;
    paragraphs: number;
    lists: number;
    codeBlocks: number;
    links: number;
    images: number;
    tables: number;
  };
  
  /** Detected markdown flavor */
  detectedFlavor: 'CommonMark' | 'GFM' | 'Mixed';
}

export interface ExportOptions {
  /** Include standalone HTML document structure (DOCTYPE, html, head, body) */
  standalone: boolean;
  
  /** Include inline CSS styles */
  includeStyles: boolean;
  
  /** CSS stylesheet to include (if includeStyles is true) */
  stylesheet?: string;
  
  /** Document title for standalone export */
  title?: string;
  
  /** Custom meta tags for standalone export */
  metaTags?: Record<string, string>;
  
  /** File name for export (without extension) */
  fileName: string;
}

// ============================================================================
// Service State Types
// ============================================================================

export interface ConversionState {
  /** Whether conversion is currently in progress */
  isConverting: boolean;
  
  /** Current conversion result (if available) */
  result?: ConversionResult;
  
  /** Input markdown being converted */
  currentInput: string;
  
  /** Applied conversion options */
  options: ConversionOptions;
}

// ============================================================================
// Helper Types
// ============================================================================

export interface ClipboardCopyResult {
  /** Whether copy was successful */
  success: boolean;
  
  /** Error message if copy failed */
  error?: string;
  
  /** Number of characters copied */
  copiedLength: number;
}

export interface SyntaxHighlightConfig {
  /** Theme name (e.g., 'prism', 'vs-dark', 'material') */
  theme: string;
  
  /** Supported language identifiers */
  supportedLanguages: string[];
  
  /** Whether to show line numbers */
  showLineNumbers: boolean;
}

// ============================================================================
// Enums
// ============================================================================

export enum ConversionWarningCode {
  /** Unsupported markdown syntax encountered */
  UNSUPPORTED_SYNTAX = 'UNSUPPORTED_SYNTAX',
  
  /** Potentially unsafe HTML was stripped */
  STRIPPED_HTML = 'STRIPPED_HTML',
  
  /** Image reference has broken or relative path */
  BROKEN_IMAGE_REFERENCE = 'BROKEN_IMAGE_REFERENCE',
  
  /** Link reference is invalid or broken */
  BROKEN_LINK_REFERENCE = 'BROKEN_LINK_REFERENCE',
  
  /** Code block missing language specification */
  MISSING_CODE_LANGUAGE = 'MISSING_CODE_LANGUAGE',
  
  /** Document size approaching maximum limit */
  LARGE_DOCUMENT = 'LARGE_DOCUMENT'
}

export enum ConversionErrorType {
  /** Input exceeds maximum allowed size */
  INPUT_TOO_LARGE = 'INPUT_TOO_LARGE',
  
  /** Markdown parsing failed */
  PARSE_ERROR = 'PARSE_ERROR',
  
  /** HTML sanitization failed */
  SANITIZATION_ERROR = 'SANITIZATION_ERROR',
  
  /** Invalid input (null, undefined, invalid type) */
  INVALID_INPUT = 'INVALID_INPUT',
  
  /** Generic conversion failure */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// ============================================================================
// Constants
// ============================================================================

/** Maximum document size (1MB) */
export const MAX_DOCUMENT_SIZE = 1048576;

/** Large document warning threshold (500KB) */
export const LARGE_DOCUMENT_THRESHOLD = 524288;

/** Default debounce time for live preview (ms) */
export const PREVIEW_DEBOUNCE_MS = 300;

/** Maximum rendering time before showing progress indicator (ms) */
export const SLOW_RENDER_THRESHOLD_MS = 1000;

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_CONVERSION_OPTIONS: ConversionOptions = {
  enableGfm: true,
  sanitize: true,
  allowRawHtml: false,
  breaks: true,
  highlightCode: false,
  cssClassPrefix: 'md-',
  maxInputSize: MAX_DOCUMENT_SIZE
};

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  standalone: true,
  includeStyles: true,
  fileName: 'changelog-export'
};

// ============================================================================
// Type Guards
// ============================================================================

export function isConversionResult(obj: any): obj is ConversionResult {
  return obj && 
         typeof obj.html === 'string' &&
         typeof obj.success === 'boolean' &&
         Array.isArray(obj.warnings);
}

export function isConversionError(obj: any): obj is ConversionError {
  return obj && 
         typeof obj.type === 'string' &&
         typeof obj.message === 'string';
}
